'use client'

import { useTheme } from '@/contexts/ThemeContext'
import { MapPin, Clock, Users, Code } from 'lucide-react'

export default function CareersPage() {
  const { theme } = useTheme()

  const positions = [
    {
      title: 'Frontend 개발자',
      type: '정규직',
      location: '서울/원격',
      experience: '3년 이상',
      skills: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS'],
      description: '사용자 경험을 최우선으로 하는 프론트엔드 개발자를 찾습니다.'
    },
    {
      title: 'Backend 개발자',
      type: '정규직',
      location: '서울/원격',
      experience: '3년 이상',
      skills: ['Node.js', 'PostgreSQL', 'Redis', 'AWS'],
      description: '안정적이고 확장 가능한 백엔드 시스템을 구축할 개발자를 찾습니다.'
    },
    {
      title: 'DevOps 엔지니어',
      type: '정규직',
      location: '서울/원격',
      experience: '4년 이상',
      skills: ['Kubernetes', 'Docker', 'CI/CD', 'Monitoring'],
      description: '안정적인 서비스 운영을 위한 인프라를 구축하고 관리할 엔지니어를 찾습니다.'
    },
    {
      title: '프리다이빙 안전 컨설턴트',
      type: '계약직',
      location: '제주/부산',
      experience: '강사급 이상',
      skills: ['프리다이빙 강사 자격증', '안전 관리 경험', '응급처치'],
      description: '다이버들의 안전을 위한 가이드라인 수립과 교육 콘텐츠 개발을 담당합니다.'
    }
  ]

  const benefits = [
    '경쟁력 있는 연봉과 스톡옵션',
    '자유로운 원격근무 및 유연근무제',
    '연 1회 팀 워크샵 (다이빙 여행)',
    '개인 발전을 위한 교육비 지원',
    '최신 장비 및 개발 환경 제공',
    '다이빙 장비 할인 혜택'
  ]

  return (
    <div className={`min-h-screen ${theme === 'light' ? 'bg-gray-50' : 'bg-gray-900'}`}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="text-center mb-16 xxs:mb-8">
          <h1 className={`text-4xl xxs:text-2xl md:text-5xl font-bold mb-6 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              함께 성장해요
            </span>
          </h1>
          <p className={`text-xl xxs:text-base max-w-3xl mx-auto ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
            바다를 사랑하고 기술로 세상을 바꾸고 싶은 사람들과 함께
            더 안전하고 즐거운 다이빙 문화를 만들어가고 있습니다.
          </p>
        </div>

        {/* 회사 문화 */}
        <div className={`mb-16 xxs:mb-8 p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}>
          <h2 className={`text-3xl xxs:text-xl font-bold mb-6 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            우리의 문화
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 xxs:gap-3">
            <div className="text-center">
              <Code className="w-12 h-12 text-blue-500 mx-auto mb-4" />
              <h3 className={`text-xl xxs:text-base font-semibold mb-3 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                기술 중심
              </h3>
              <p className={`${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                최신 기술과 모범 사례를 적극 도입하여 지속적으로 성장합니다.
              </p>
            </div>
            <div className="text-center">
              <Users className="w-12 h-12 text-blue-500 mx-auto mb-4" />
              <h3 className={`text-xl xxs:text-base font-semibold mb-3 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                협업과 소통
              </h3>
              <p className={`${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                투명한 소통과 적극적인 협업으로 더 나은 결과를 만듭니다.
              </p>
            </div>
            <div className="text-center">
              <MapPin className="w-12 h-12 text-blue-500 mx-auto mb-4" />
              <h3 className={`text-xl xxs:text-base font-semibold mb-3 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                워라밸
              </h3>
              <p className={`${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                일과 삶의 균형을 통해 지속 가능한 성장을 추구합니다.
              </p>
            </div>
          </div>
        </div>

        {/* 채용 포지션 */}
        <div className="mb-16 xxs:mb-8">
          <h2 className={`text-3xl xxs:text-xl font-bold mb-8 xxs:mb-4 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            채용 중인 포지션
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 xxs:gap-3">
            {positions.map((position, index) => (
              <div
                key={index}
                className={`p-6 xxs:p-3 rounded-xl ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className={`text-xl xxs:text-base font-semibold ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                    {position.title}
                  </h3>
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    {position.type}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 text-gray-500 mr-2" />
                    <span className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>
                      {position.location}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 text-gray-500 mr-2" />
                    <span className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>
                      경력 {position.experience}
                    </span>
                  </div>
                </div>

                <p className={`mb-4 ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
                  {position.description}
                </p>

                <div className="mb-4">
                  <h4 className={`text-sm font-medium mb-2 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
                    요구 기술
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {position.skills.map((skill, skillIndex) => (
                      <span
                        key={skillIndex}
                        className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md transition-colors">
                  지원하기
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* 복리후생 */}
        <div className={`mb-16 xxs:mb-8 p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-white shadow-md' : 'bg-gray-800'}`}>
          <h2 className={`text-3xl xxs:text-xl font-bold mb-6 text-center ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            복리후생
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                <span className={`${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
                  {benefit}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 지원 방법 */}
        <div className={`text-center p-8 xxs:p-4 rounded-xl ${theme === 'light' ? 'bg-blue-50' : 'bg-blue-900/20'}`}>
          <h2 className={`text-2xl xxs:text-lg font-bold mb-4 ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>
            지원 방법
          </h2>
          <p className={`mb-6 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
            관심 있는 포지션이 있으시다면 이력서와 포트폴리오를 함께 보내주세요.
          </p>
          <div className="space-y-2">
            <p className={`${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
              이메일: careers@todaydiving.com
            </p>
            <p className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
              * 모든 지원서는 검토 후 2주 이내에 회신드립니다
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}